import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { authApi, AuthApiError } from '../../api/authApi';
import { ME_QUERY_KEY } from '../../queries/useMe';
import { useAuthStore } from '../../store/useAuthStore';
import { useFingerprintStore } from '../../store/useFingerprintStore';

const BLOCKING_CODES = new Set(['rate_limited']);

export function SignUpTab(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const closeModal = useAuthStore((s) => s.closeAuthModal);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    if (blockedUntil === null) return;
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [blockedUntil]);

  const isBlocked = blockedUntil !== null && now < blockedUntil;
  const secondsRemaining = isBlocked && blockedUntil !== null ? blockedUntil - now : 0;

  useEffect(() => {
    if (blockedUntil !== null && now >= blockedUntil) {
      setBlockedUntil(null);
      setError(null);
    }
  }, [now, blockedUntil]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (isBlocked) return;
    setLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      const fingerprintHex = useFingerprintStore.getState().hash_hex;
      const data = await authApi.register({
        email,
        password,
        ...(fingerprintHex ? { fingerprint_hash: fingerprintHex } : {}),
      });
      setUser(
        {
          userId: data.user_id,
          nickname: data.nickname ?? null,
          email: data.email ?? null,
          authMethod: 'email',
          tierId: 2,
          tierKey: 'regular',
          ageVerificationStatus: 'pending',
        },
        data.csrf_token,
      );
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      closeModal();
      navigate('/match');
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.status === 429 && BLOCKING_CODES.has(err.code)) {
          const retryAfter = Number(err.details?.['retry_after'] ?? 3600);
          setBlockedUntil(Math.floor(Date.now() / 1000) + retryAfter);
          setNow(Math.floor(Date.now() / 1000));
          setError(err.message);
        } else if (err.status === 400 && err.details) {
          setFieldErrors(err.details as Record<string, string>);
        } else {
          setError(err.message);
        }
      } else {
        setError('Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel = (() => {
    if (loading) return 'Creating account…';
    if (isBlocked) return `Try again in ${secondsRemaining}s`;
    return 'Sign Up';
  })();

  return (
    <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={Boolean(fieldErrors['email'])}
        helperText={fieldErrors['email']}
        autoFocus
        required
        fullWidth
        disabled={isBlocked}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={Boolean(fieldErrors['password'])}
        helperText={fieldErrors['password'] ?? 'Minimum 8 characters, at least one letter and one digit'}
        required
        fullWidth
        disabled={isBlocked}
      />
      {error && <Alert severity={isBlocked ? 'warning' : 'error'}>{error}</Alert>}
      <Button type="submit" variant="contained" size="large" disabled={loading || isBlocked} fullWidth>
        {buttonLabel}
      </Button>
    </Box>
  );
}
